import React, { useEffect, useRef, useState } from "react";
import {
  Search,
  Heart,
  ShoppingCart,
  Menu,
  X,
  User,
  ChevronDown,
  ChevronRight,
  Bell,
  CreditCard,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import HeaderMenu from "../HeaderMenu";
import BaseApi from "../../services/base-api";
import "../../styles/Header.css";
import { useMediaQuery, useTheme } from "@mui/material";
import CurrencySelector from "../Currency/CurrencySelector";
import {
  Instagram,
  LinkedIn,
  QuestionAnswer,
  Twitter,
} from "@mui/icons-material";
import AppLogo from "../AppLogo";
import { SocialLinkData } from "../../pages/NonCustomer/CompanySettingsPage";
import { defaultSocialLinks } from "../Footer";
import MyFatoorahPayment from "../../pages/MyFatoorahPayment";
import Price from "../Currency/Price";

interface SubMenuItem {
  label: string;
  route: string;
}

interface MenuItem {
  label: string;
  route: string;
  subItems?: SubMenuItem[];
}

interface PendingPaymentNotification {
  id: string;
  orderId: string;
  orderNumber?: string;
  amount: number;
  paymentMethod: string;
  title?: string;
  titleAr?: string;
  message?: string;
  messageAr?: string;
  createdAt?: string;
}

const getPendingPaymentNotificationItems = (payload: any): any[] => {
  const data = payload?.data ?? payload;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.notifications)) return data.notifications;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.pendingPayments)) return data.pendingPayments;
  if (Array.isArray(data?.pendingNotifications))
    return data.pendingNotifications;
  if (Array.isArray(payload?.notifications)) return payload.notifications;

  return [];
};

const normalizePendingPaymentNotification = (
  item: any,
  index: number,
): PendingPaymentNotification | null => {
  const orderIdValue =
    item?.orderId ??
    item?.order?.id ??
    item?.order?.orderId ??
    item?.invoice?.orderId;
  const orderId =
    typeof orderIdValue === "string"
      ? orderIdValue
      : orderIdValue !== undefined && orderIdValue !== null
        ? String(orderIdValue)
        : "";

  if (!orderId) return null;

  const rawAmount =
    item?.amount ??
    item?.total ??
    item?.orderTotal ??
    item?.remainingAmount ??
    item?.invoice?.total ??
    item?.order?.total ??
    0;
  const amount = Number(rawAmount);
  const orderNumberValue =
    item?.orderNumber ?? item?.order?.orderNumber ?? item?.invoice?.orderNumber;
  const titleValue = item?.title ?? item?.heading ?? item?.subject;
  const messageValue =
    item?.message ?? item?.description ?? item?.body ?? item?.content;
  const paymentMethodValue = item?.paymentMethod ?? item?.order?.paymentMethod;

  return {
    id: String(item?.id ?? item?.notificationId ?? `${orderId}-${index}`),
    orderId,
    orderNumber: orderNumberValue ? String(orderNumberValue) : undefined,
    amount: Number.isFinite(amount) ? amount : 0,
    paymentMethod: paymentMethodValue ? String(paymentMethodValue) : "card",
    title: titleValue ? String(titleValue) : undefined,
    titleAr:
      item?.titleAr || item?.title_ar || item?.headingAr || item?.subjectAr,
    message: messageValue ? String(messageValue) : undefined,
    messageAr:
      item?.messageAr ||
      item?.message_ar ||
      item?.descriptionAr ||
      item?.bodyAr ||
      item?.contentAr,
    createdAt:
      item?.createdAt ??
      item?.updatedAt ??
      item?.date ??
      item?.notifiedAt ??
      undefined,
  };
};

const extractPendingPaymentNotifications = (
  payload: any,
): PendingPaymentNotification[] =>
  getPendingPaymentNotificationItems(payload)
    .map(normalizePendingPaymentNotification)
    .filter(
      (notification): notification is PendingPaymentNotification =>
        notification !== null,
    );

const Header: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [useApiData, setUseApiData] = useState(false);
  const [socialLinksData, setSocialLinksData] = useState<
    Record<string, SocialLinkData>
  >({});
  const [searchQuery, setSearchQuery] = useState("");
  const [wishlistCount, setWishlistCount] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [pendingPaymentNotifications, setPendingPaymentNotifications] =
    useState<PendingPaymentNotification[]>([]);
  const [activePaymentNotification, setActivePaymentNotification] =
    useState<PendingPaymentNotification | null>(null);
  const [notificationDropdownTop, setNotificationDropdownTop] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());

  const { t, i18n } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();
  const { err } = useToast();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isArabic = i18n.language === "ar";
  const headerRef = useRef<HTMLElement | null>(null);
  const notificationDropdownRef = useRef<HTMLDivElement | null>(null);

  let timeoutId: NodeJS.Timeout;

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchFavoritesCount();
      fetchCartCount();
      fetchPendingPaymentNotifications();
    } else {
      setWishlistCount(0);
      setCartCount(0);
      setPendingPaymentNotifications([]);
      setIsNotificationsOpen(false);
      setActivePaymentNotification(null);
    }

    fetchSocialLinks();

    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    const handleCartUpdate = (event: Event) => {
      const { detail } = event as CustomEvent<{ count?: number }>;
      if (typeof detail?.count === "number") {
        setCartCount(detail.count);
      } else {
        fetchCartCount();
      }
      animateBadge(".cart-badge");
    };

    const handleWishlistUpdate = () => {
      fetchFavoritesCount();
      animateBadge(".wishlist-badge");
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("cartUpdated", handleCartUpdate as EventListener);
    window.addEventListener(
      "wishlistUpdated",
      handleWishlistUpdate as EventListener,
    );

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener(
        "cartUpdated",
        handleCartUpdate as EventListener,
      );
      window.removeEventListener(
        "wishlistUpdated",
        handleWishlistUpdate as EventListener,
      );
    };
  }, [isAuthenticated, user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationDropdownRef.current &&
        !notificationDropdownRef.current.contains(event.target as Node)
      ) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isNotificationsOpen || !isMobile) return;

    const updateNotificationDropdownPosition = () => {
      const headerBottom =
        headerRef.current?.getBoundingClientRect().bottom ?? 0;
      setNotificationDropdownTop(Math.ceil(headerBottom + 8));
    };

    updateNotificationDropdownPosition();
    window.addEventListener("resize", updateNotificationDropdownPosition);
    window.addEventListener("scroll", updateNotificationDropdownPosition);

    return () => {
      window.removeEventListener("resize", updateNotificationDropdownPosition);
      window.removeEventListener("scroll", updateNotificationDropdownPosition);
    };
  }, [isNotificationsOpen, isMobile]);

  const fetchSocialLinks = async () => {
    try {
      setLoading(true);
      const res = await BaseApi.get("/social-links");

      if (res.success && res.data && res.data.length > 0) {
        const updatedLinks: Record<string, SocialLinkData> = {};
        let hasValidData = false;

        res.data.forEach((link: SocialLinkData) => {
          // Store each social link by platform
          updatedLinks[link.platform] = link;
          // Check if at least one link has a valid URL
          if (link.url && link.url.trim() !== "") {
            hasValidData = true;
          }
        });

        if (hasValidData) {
          setSocialLinksData(updatedLinks);
          setUseApiData(true);
        } else {
          // No valid URLs in API data, use default
          setUseApiData(false);
        }
      } else {
        // API call successful but no data, use default
        setUseApiData(false);
      }
    } catch (error) {
      console.error("Error fetching social links:", error);
      // On error, use default hardcoded links
      setUseApiData(false);
    } finally {
      setLoading(false);
    }
  };

  // Get sorted and active social links from API
  const getActiveSocialLinks = () => {
    return Object.values(socialLinksData)
      .filter((link) => link.url && link.url.trim() !== "")
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  };

  // Get the social links to display (API or default)
  const getSocialLinksToDisplay = () => {
    if (useApiData) {
      return getActiveSocialLinks();
    }
    return defaultSocialLinks;
  };

  // Render TikTok icon
  const TikTokIcon = () => (
    <svg
      className="w-5 h-5 group-hover:scale-110 transition-transform"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  );

  // Render social icon based on platform
  const renderSocialIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case "instagram":
        return (
          <Instagram className="w-5 h-5 group-hover:scale-110 transition-transform" />
        );
      case "twitter":
        return (
          <Twitter className="w-5 h-5 group-hover:scale-110 transition-transform" />
        );
      case "linkedin":
        return (
          <LinkedIn className="w-5 h-5 group-hover:scale-110 transition-transform" />
        );
      case "tiktok":
        return <TikTokIcon />;
      default:
        return (
          <QuestionAnswer className="w-5 h-5 group-hover:scale-110 transition-transform" />
        );
    }
  };

  const animateBadge = (selector: string) => {
    const badge = document.querySelector(selector);
    if (badge) {
      badge.classList.remove("badge-jump");
      void (badge as HTMLElement).offsetWidth;
      badge.classList.add("badge-jump");
    }
  };

  const fetchFavoritesCount = async (): Promise<void> => {
    try {
      const res = await BaseApi.get("/favorites/count");
      if (res.success && res?.data) {
        setWishlistCount(res.data.count);
      }
    } catch (error) {
      console.error("Failed to fetch favorites:", error);
    }
  };

  const fetchCartCount = async (): Promise<void> => {
    try {
      const res = await BaseApi.get("/cart/count");
      if (res.success) {
        setCartCount(res.data.count);
      }
    } catch (error) {
      console.error("Failed to load cart items:", error);
    }
  };

  const fetchPendingPaymentNotifications = async (): Promise<void> => {
    try {
      setLoadingNotifications(true);
      const res = await BaseApi.get(
        "/payments/notifications/pending",
        {},
        { skipLoader: true },
      );
      setPendingPaymentNotifications(extractPendingPaymentNotifications(res));
    } catch (error) {
      console.error("Failed to fetch pending payment notifications:", error);
      setPendingPaymentNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const toggleNotifications = () => {
    setIsNotificationsOpen((prev) => !prev);
  };

  const handleNotificationPayment = (
    notification: PendingPaymentNotification,
  ) => {
    if (!notification.orderId || notification.amount <= 0) {
      err(t("Unable to start payment for this order"));
      return;
    }

    setActivePaymentNotification(notification);
    setIsNotificationsOpen(false);
  };

  const handleNotificationPaymentSuccess = () => {
    setActivePaymentNotification(null);
    fetchPendingPaymentNotifications();
  };

  const handleNotificationPaymentCancel = () => {
    setActivePaymentNotification(null);
  };

  const handleNotificationPaymentError = (errorMessage: string) => {
    console.error("Payment error:", errorMessage);
    err(errorMessage || t("Payment failed. Please try again."));
  };

  const formatNotificationDate = (dateString?: string) => {
    if (!dateString) return null;

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;

    return date.toLocaleString(isArabic ? "ar-QA" : "en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const changeLanguage = (lang: "en" | "ar") => {
    i18n.changeLanguage(lang);
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  };

  const handleOpenCart = () => {
    navigate("/account/cart");
  };

  const handleOpenMyRatings = () => {
    navigate("/account/my-ratings");
  };

  const handleProfileNavigation = (route: string) => {
    navigate(`/${route.replace(/^\//, "")}`);
    setIsProfileOpen(false);
    setIsDrawerOpen(false);
    setExpandedMenus(new Set());
  };

  const handleLogout = () => {
    logout();
    setIsProfileOpen(false);
    setIsDrawerOpen(false);
    navigate("/");
  };

  const toggleMenuExpansion = (menuLabel: string) => {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(menuLabel)) {
        newSet.delete(menuLabel);
      } else {
        newSet.add(menuLabel);
      }
      return newSet;
    });
  };

  const getUserDisplayName = () => {
    // If we are authenticated but the user object hasn't loaded yet
    if (isAuthenticated && !user) return t("Loading...");

    if (!user) return t("User");

    const { firstName, identifier } = user;

    if (firstName && firstName.trim() !== "") {
      return firstName;
    }

    // Fallback to email/username if firstName is missing
    return identifier ? identifier.split("@")[0] : t("User");
  };

  // Handle search submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // Handle search input key press (Enter key)
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch(e as any);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setIsSearchFocused(false);

    // Optional: reset URL to products page without query
    // only reset URL if user was already searching
    const params = new URLSearchParams(window.location.search);
    if (params.get("search")) {
      navigate("/products");
    }
  };

  const menuItems: MenuItem[] = isAuthenticated
    ? [
        { label: t("Home"), route: "/" },
        { label: t("All Products"), route: "/products" },
        {
          label: t("My Account"),
          route: "",
          subItems: [
            { label: t("Profile"), route: "account/profile" },
            { label: t("Orders"), route: "account/orders" },
            { label: t("Returns and Exchanges"), route: "account/returns" },
            { label: t("Address"), route: "account/address" },
            // { label: `${t("Favorites")} (${wishlistCount})`, route: "account/favorites" },
          ],
        },
        { label: t("About us"), route: "/about" },
        { label: t("Contact us"), route: "/contact" },
        { label: t("Return and exchange policy"), route: "/privacy-policy" },
      ]
    : [
        { label: t("Home"), route: "/" },
        { label: t("All products"), route: "/products" },
        { label: t("About us"), route: "/about" },
        { label: t("Contact us"), route: "/contact" },
        { label: t("Return and exchange policy"), route: "/privacy-policy" },
        { label: t("Sign In"), route: "signin" },
        { label: t("Sign Up"), route: "signin" },
      ];

  const socialLinksToDisplay = getSocialLinksToDisplay();
  const hasPendingNotifications = pendingPaymentNotifications.length > 0;

  return (
    <div>
      <header
        ref={headerRef}
        className={`header-sticky ${scrolled ? "header-scrolled" : ""}`}
        style={{
          backgroundColor: "#f2e6db",
          boxShadow: scrolled
            ? "0 4px 12px rgba(0, 0, 0, 0.15)"
            : "0 2px 8px rgba(0, 0, 0, 0.1)",
          transition: "box-shadow 0.3s ease",
        }}
        dir="ltr"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between gap-3 sm:gap-6">
            {/* Logo */}

            <AppLogo className="w-32 h-32" />

            {/* Search Bar - Desktop */}
            <div className="hidden md:flex flex-1 max-w-2xl relative">
              <form
                onSubmit={handleSearch}
                className={`search-container w-full ${
                  i18n.language === "ar" ? "rtl text-right" : "ltr text-left"
                }`}
              >
                <Search
                  className={`search-icon ${
                    i18n.language === "ar" ? "right-4" : "left-4"
                  } ${isSearchFocused ? "text-black scale-110" : ""}`}
                  onClick={handleSearch}
                  style={{ cursor: "pointer" }}
                />
                <input
                  type="text"
                  placeholder={t("product_search")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  onKeyPress={handleSearchKeyPress}
                  className={`search-input ${
                    i18n.language === "ar" ? "pr-12 pl-4" : "pl-12 pr-4"
                  }`}
                  dir={i18n.language === "ar" ? "rtl" : "ltr"}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </form>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 sm:gap-3">
              {isAuthenticated && (
                <div className="relative" ref={notificationDropdownRef}>
                  <button
                    type="button"
                    onClick={toggleNotifications}
                    className="btn-base bg-white text-[#0d223f] border border-[#0d223f]/10 relative"
                    aria-label={t("Notifications")}
                    aria-expanded={isNotificationsOpen}
                  >
                    <Bell
                      className={`icon w-5 h-5 ${
                        hasPendingNotifications ? "notification-bell-alert" : ""
                      }`}
                    />
                    {hasPendingNotifications && (
                      <span className="notification-dot" aria-hidden="true" />
                    )}
                  </button>

                  {isNotificationsOpen && (
                    <div
                      className="notification-dropdown fixed left-3 right-3 rounded-2xl border border-gray-200 bg-white shadow-2xl z-50 overflow-hidden md:absolute md:left-auto md:right-0 md:mt-3 md:w-[24rem]"
                      dir={isArabic ? "rtl" : "ltr"}
                      style={
                        isMobile
                          ? {
                              top: `${notificationDropdownTop}px`,
                              maxHeight: `calc(100vh - ${notificationDropdownTop + 12}px)`,
                            }
                          : undefined
                      }
                    >
                      <div
                        className={`flex items-center justify-between px-4 py-3 border-b border-gray-100 ${
                          isArabic ? "text-right" : "text-left"
                        }`}
                      >
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">
                            {t("Notifications")}
                          </h3>
                          <p className="text-xs text-gray-500">
                            {t("Pending payment notifications")}
                          </p>
                        </div>
                        {loadingNotifications && (
                          <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
                        )}
                      </div>

                      <div className="max-h-96 overflow-y-auto">
                        {!loadingNotifications &&
                        pendingPaymentNotifications.length === 0 ? (
                          <div className="px-4 py-6 text-sm text-gray-500 text-center">
                            {t("No pending payment notifications")}
                          </div>
                        ) : (
                          pendingPaymentNotifications.map((notification) => {
                            const formattedDate = formatNotificationDate(
                              notification.createdAt,
                            );

                            return (
                              <div
                                key={notification.id}
                                className="px-4 py-4 border-b border-gray-100 last:border-b-0"
                              >
                                <div
                                  className={`flex items-start gap-3 ${
                                    isArabic
                                      ? "flex-row-reverse text-right"
                                      : ""
                                  }`}
                                >
                                  <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600">
                                    <CreditCard className="w-5 h-5" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div
                                      className={`flex items-start justify-between gap-3 ${
                                        isArabic ? "flex-row-reverse" : ""
                                      }`}
                                    >
                                      <div className="min-w-0">
                                        <p className="text-sm font-semibold text-gray-900 truncate">
                                          {isArabic
                                            ? notification.titleAr ||
                                              t("Pending payment notification")
                                            : notification.title ||
                                              t("Pending payment notification")}
                                        </p>
                                        <p className="mt-1 text-sm text-gray-600">
                                          {isArabic
                                            ? notification.messageAr ||
                                              t(
                                                "Complete the payment for this order.",
                                              )
                                            : notification.message ||
                                              t(
                                                "Complete the payment for this order.",
                                              )}
                                        </p>
                                      </div>
                                      {notification.amount > 0 && (
                                        <div className="shrink-0 text-sm font-semibold text-gray-900">
                                          <Price amount={notification.amount} />
                                        </div>
                                      )}
                                    </div>

                                    <div
                                      className={`mt-2 flex items-center justify-between gap-3 ${
                                        isArabic ? "flex-row-reverse" : ""
                                      }`}
                                    >
                                      <div
                                        className={`text-xs text-gray-500 ${
                                          isArabic ? "text-right" : "text-left"
                                        }`}
                                      >
                                        <span
                                          dir="ltr"
                                          className="font-medium text-gray-700 inline-block"
                                        >
                                          #
                                          {notification.orderNumber ||
                                            notification.orderId}
                                        </span>
                                        {formattedDate && (
                                          <span
                                            dir="ltr"
                                            className={`inline-block ${
                                              isArabic ? "mr-2" : "ml-2"
                                            }`}
                                          >
                                            {formattedDate}
                                          </span>
                                        )}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleNotificationPayment(
                                            notification,
                                          )
                                        }
                                        className={`inline-flex items-center gap-2 rounded-lg bg-[#0d223f] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#16345d] ${
                                          isArabic ? "flex-row-reverse" : ""
                                        }`}
                                      >
                                        <CreditCard className="w-4 h-4" />
                                        {t("Pay now")}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Profile - Desktop */}
              <div
                className="relative hidden lg:block"
                onMouseEnter={() => {
                  clearTimeout(timeoutId);
                  setIsProfileOpen(true);
                }}
                onMouseLeave={() => {
                  timeoutId = setTimeout(() => setIsProfileOpen(false), 400);
                }}
              >
                <button className="btn-profile bg-[#0d223f] flex items-center gap-1 px-3 py-3 rounded-xl border border-primary-300 transition-all duration-300">
                  <User className="w-5 h-5" />
                  <span className="text-sm font-semibold">
                    {isAuthenticated
                      ? t("Hi,") + ` ${getUserDisplayName().split(" ")[0]}`
                      : t("Account")}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-300 ${
                      isProfileOpen ? "rotate-180" : ""
                    }`}
                    style={{ transformOrigin: "center" }}
                  />
                </button>

                {isProfileOpen && (
                  <div className="dropdown-container">
                    <div className="dropdown-menu">
                      {!isAuthenticated ? (
                        <>
                          <button
                            onClick={() => handleProfileNavigation("signin")}
                            className="dropdown-item w-full text-left"
                          >
                            {t("Sign In")}
                          </button>
                          <button
                            onClick={() => handleProfileNavigation("signin")}
                            className="dropdown-item w-full text-left"
                          >
                            {t("Sign Up")}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() =>
                              handleProfileNavigation("account/profile")
                            }
                            className="dropdown-item w-full text-left"
                          >
                            {t("My Profile")}
                          </button>
                          <button
                            onClick={() =>
                              handleProfileNavigation("account/orders")
                            }
                            className="dropdown-item w-full text-left"
                          >
                            {t("Orders")}
                          </button>
                          <button
                            onClick={() =>
                              handleProfileNavigation("account/returns")
                            }
                            className="dropdown-item w-full text-left"
                          >
                            {t("Returns and Exchanges")}
                          </button>
                          <button
                            onClick={() =>
                              handleProfileNavigation("account/address")
                            }
                            className="dropdown-item w-full text-left"
                          >
                            {t("Address")}
                          </button>
                          <button
                            onClick={handleOpenMyRatings}
                            className="dropdown-item w-full text-left"
                          >
                            {t("My Ratings")}
                          </button>
                          {/* <button
                            onClick={() =>
                              handleProfileNavigation("account/favorites")
                            }
                            className="dropdown-item w-full text-left"
                          >
                            {t("Favorites")} ({wishlistCount})
                          </button> */}
                          <hr className="my-2" />
                          <button
                            onClick={handleLogout}
                            className="w-full text-left px-5 py-3.5 text-red-600 hover:bg-red-50 rounded-xl font-bold transition-colors duration-200"
                          >
                            {t("Logout")}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Wishlist */}
              <button
                onClick={() => handleProfileNavigation("account/favorites")}
                className="btn-base btn-wishlist relative"
              >
                <Heart className="icon w-5 h-5" />
                {wishlistCount > 0 && (
                  <span className="badge wishlist-badge">{wishlistCount}</span>
                )}
              </button>

              {/* Cart Button with Badge */}
              <button
                onClick={handleOpenCart}
                className="btn-base bg-[#0d223f] btn-cart relative"
              >
                <ShoppingCart className="icon w-5 h-5" />
                {cartCount > 0 && (
                  <span className="badge cart-badge">{cartCount}</span>
                )}
              </button>

              {/* Hamburger - Mobile */}
              <button
                onClick={() => setIsDrawerOpen(true)}
                className="icon-btn lg:hidden"
              >
                <Menu className="w-6 h-6" />
              </button>

              {!isMobile && (
                <div>
                  <select
                    className="language-select"
                    value={i18n.language}
                    onChange={(e) =>
                      changeLanguage(e.target.value as "en" | "ar")
                    }
                  >
                    <option value="en">English</option>
                    <option value="ar">العربية</option>
                  </select>
                </div>
              )}

              {/* Currency Selector */}
              {!isMobile && (
                <div>
                  <CurrencySelector className="w-full" />
                </div>
              )}
            </div>
          </div>

          {/* Search Bar - Mobile */}
          <div className="md:hidden">
            <form
              onSubmit={handleSearch}
              className={`search-container ${
                i18n.language === "ar" ? "rtl text-right" : "ltr text-left"
              }`}
            >
              <Search
                className={`search-icon ${
                  i18n.language === "ar" ? "right-4" : "left-4"
                } ${isSearchFocused ? "text-black scale-110" : ""}`}
                onClick={handleSearch}
                style={{ cursor: "pointer" }}
              />
              <input
                type="text"
                placeholder={t("product_search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                onKeyPress={handleSearchKeyPress}
                className={`search-input ${
                  i18n.language === "ar"
                    ? "pr-11 pl-4 text-right"
                    : "pl-11 pr-4 text-left"
                }`}
                dir={i18n.language === "ar" ? "rtl" : "ltr"}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </form>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      {isDrawerOpen && isMobile && (
        <>
          {/* Overlay */}
          <div
            className="mobile-drawer-overlay"
            onClick={() => {
              setIsDrawerOpen(false);
              setExpandedMenus(new Set());
            }}
          />

          {/* Drawer Panel */}
          <div className="mobile-drawer-panel">
            <div className="p-6 h-full flex flex-col">
              {/* Header with Close Button */}
              <div className="flex items-center justify-between">
                {/* User Info */}
                <div
                  className="flex items-center gap-3 bg-gradient-to-r from-amber-100 to-amber-50 rounded-2xl p-4 shadow-md"
                  onClick={() => handleProfileNavigation("signin")}
                >
                  <div className="bg-amber-600 p-2 rounded-full">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-lg text-gray-800">
                      {isAuthenticated
                        ? getUserDisplayName()
                        : t("Welcome, Guest")}
                    </p>
                    <p className="text-sm text-gray-600">
                      {isAuthenticated
                        ? t("Explore your account")
                        : t("Sign in to continue")}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setIsDrawerOpen(false);
                    setExpandedMenus(new Set());
                  }}
                  className="drawer-close-btn"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Scrollable Menu Items */}
              <div className="flex-1 overflow-y-auto mt-6">
                <div className="space-y-2">
                  {menuItems.map((item, idx) => (
                    <div key={idx}>
                      {/* Main Menu Item */}
                      <button
                        onClick={() => {
                          if (item.subItems) {
                            toggleMenuExpansion(item.label);
                          } else {
                            handleProfileNavigation(item.route);
                          }
                        }}
                        className="drawer-menu-item w-full flex justify-between items-center"
                      >
                        <span>{item.label}</span>
                        {item.subItems && (
                          <ChevronRight
                            className={`w-4 h-4 transition-transform duration-300 ${
                              expandedMenus.has(item.label) ? "rotate-90" : ""
                            }`}
                          />
                        )}
                      </button>

                      {/* Sub Menu Items (Expandable) */}
                      {item.subItems && (
                        <div
                          className={`overflow-hidden transition-all duration-300 ${
                            expandedMenus.has(item.label)
                              ? "max-h-96 opacity-100"
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          <div className="ml-4 mt-2 space-y-1 border-l-2 border-amber-200 pl-4">
                            {item.subItems.map((subItem, subIdx) => (
                              <button
                                key={subIdx}
                                onClick={() =>
                                  handleProfileNavigation(subItem.route)
                                }
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 rounded-lg transition-colors duration-200"
                              >
                                {subItem.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer Section */}
              <div className="mt-6 pt-6 border-t border-gray-600 space-y-4">
                {/* Language Selector */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Language / اللغة
                  </label>
                  <select
                    className="language-select w-full"
                    value={i18n.language}
                    onChange={(e) =>
                      changeLanguage(e.target.value as "en" | "ar")
                    }
                  >
                    <option value="en">English</option>
                    <option value="ar">العربية</option>
                  </select>
                </div>

                {/* Currency Selector */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    {t("Currency")}
                  </label>
                  <CurrencySelector className="w-full" />
                </div>

                {/* Logout */}
                {isAuthenticated && (
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-5 py-3.5 text-red-600 hover:bg-red-50 rounded-xl font-bold transition-colors duration-200"
                  >
                    {t("Logout")}
                  </button>
                )}

                {/* Follow Us Section - Dynamic Social Links with Fallback */}
                <div className="border-t border-gray-600 pt-5">
                  <h3 className="text-sm font-bold text-gray-700 mb-3">
                    {t("Follow us on")}
                  </h3>
                  {loading ? (
                    <div className="text-gray-600 text-sm text-center py-2">
                      {t("Loading...")}
                    </div>
                  ) : (
                    <div className="flex flex-row items-center justify-center gap-6">
                      {socialLinksToDisplay.map((link) => (
                        <a
                          key={link.platform}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-gray-700 hover:text-black transition-all duration-300 group"
                          title={
                            useApiData
                              ? (link as SocialLinkData).description ||
                                link.displayName
                              : link.displayName
                          }
                        >
                          {renderSocialIcon(link.platform)}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Header Menu */}
      <HeaderMenu />

      {activePaymentNotification && (
        <MyFatoorahPayment
          orderId={activePaymentNotification.orderId}
          amount={activePaymentNotification.amount}
          paymentMethod={activePaymentNotification.paymentMethod}
          onSuccess={handleNotificationPaymentSuccess}
          onCancel={handleNotificationPaymentCancel}
          onError={handleNotificationPaymentError}
        />
      )}
    </div>
  );
};

export default Header;
